import { Dispatch } from 'react';
import { Platform } from 'react-native';
import { AnyAction } from 'redux';
import find from 'lodash/find';
import { consumerIdSelector } from 'store/profile/selectors';
import { RootState, RootDispatch, GetState, Thunk } from 'store/types';
import { dfdAccessTokenSelector, fhirCurrentUserPatientId } from 'store/authentication/selectors';
import { apiKeySelector } from 'store/appSettings/selectors';
import DeviceInfoServices from 'services/DeviceInfoServices';
import FileServices from 'services/FileServices';
import Config from 'react-native-config';
import {
  Action,
  AdapterSource,
  DispatcherCategoryService,
  DispatcherAuthPrefix,
  DispatcherResource,
  DispatcherEndpoint
} from 'modules/types/common';
import { DFDHeaders } from 'serverTypes/authentication';
import { getDispatcherUrl } from 'modules/utils/UrlUtils';
import { Category } from 'store/appSettings/types';
import { currentAccountConsumerIdSelector } from 'store/account/selectors';
import { AuditData, AuditLogOptions, AuditFilterParams } from './types';
import {
  auditRowCountSelector,
  auditPagesSelector,
  auditActionsDataSelector,
  auditCategoriesDataSelector,
  auditLogFilterOptionsSelector,
  auditLogFiltersDownloadStatus
} from './selectors';

export const GET_AUDITS = 'AUDIT.GET_AUDITS';
export const GET_AUDITS_SUCCESS = 'AUDIT.GET_AUDITS_SUCCESS';
export const GET_AUDITS_FAIL = 'AUDIT.GET_AUDITS_FAIL';

interface GetAuditsProps {
  pageNumber?: number;
  totalRowCount?: number;
  options?: AuditLogOptions;
}

export const getParamsString = <T extends { [key: string]: Array<string | boolean> }>(obj: T) => {
  const str = [];
  // eslint-disable-next-line no-restricted-syntax
  for (const p in obj) {
    // eslint-disable-next-line no-prototype-builtins
    if (obj.hasOwnProperty(p)) {
      str.push(`${p}=${obj[p].length === 0 ? '[]' : `[${obj[p].toString()}]`}`);
    }
  }
  return str.join('&');
};

const getFilterParams = (options: AuditLogOptions | undefined, state: RootState) => {
  const actionsData = auditActionsDataSelector(state);
  const categoriesData = auditCategoriesDataSelector(state);
  const filterParams: AuditFilterParams = {
    performedOn: [],
    actions: [],
    categories: [],
    performedBy: []
  };

  options?.facets?.forEach(facet => {
    if (facet.group === 'performedBy') {
      filterParams.performedBy.push(facet.value);
    }

    if (facet.group === 'categories') {
      const findObj = <Category>find(categoriesData, { value: facet.value });
      if (findObj) {
        filterParams.categories.push(findObj.key);
      }
    }

    if (facet.group === 'actions') {
      const findObj = <Category>find(actionsData, { value: facet.value });
      if (findObj) {
        filterParams.actions.push(findObj.key);
      }
    }

    if (facet.group === 'persons') {
      filterParams.performedOn.push(facet.value);
    }
  });

  // Updating this date range temporarily from one year to one week for PI certification
  const pastDate = new Date();
  // const pastYear = pastDate.getFullYear() - 1;
  // pastDate.setFullYear(pastYear);
  const pastWeek = new Date(pastDate.setDate(pastDate.getDate() - 7));

  return {
    filterParams,
    endsAt: options?.endsAt ? (options.endsAt as Date).toISOString() : new Date().toISOString(),
    startsAt: options?.startsAt ? (options.startsAt as Date).toISOString() : pastWeek.toISOString()
  };
};

export const getAudits = (props: GetAuditsProps) => (
  dispatch: Dispatch<AnyAction>,
  getState: GetState
) => {
  const useExpansion = Config.AUDIT_LOG_EXPANSION;
  const AUDIT_FILTER_URL = getDispatcherUrl(
    DispatcherCategoryService.UTILITY,
    DispatcherAuthPrefix.AUTHENTICATED,
    DispatcherResource.AUDIT,
    DispatcherEndpoint.FILTER,
    true
  );

  const { pageNumber = 0, options } = props;
  const state = getState();
  const filterParams = getFilterParams(options, state);
  const searchParams = getParamsString({ ...filterParams.filterParams });

  const payload = useExpansion
    ? {
        client: 'dispatcher',
        request: {
          method: 'post',
          url: AUDIT_FILTER_URL,
          headers: { [DFDHeaders.mhpAdapter]: AdapterSource.MHP },
          data: {
            ...filterParams.filterParams,
            startsAt: filterParams.startsAt,
            endsAt: filterParams.endsAt,
            pageNumber
          }
        }
      }
    : {
        client: 'audit',
        request: {
          method: 'get',
          url: `/filter?${encodeURI(searchParams)}&startsAt=${filterParams.startsAt}&endsAt=${
            filterParams.endsAt
          }&pageNumber=${pageNumber}&pageSize=${100}`,
          headers: {
            'x-dfd-consumer-id': consumerIdSelector(getState())
          }
        }
      };

  return dispatch({
    type: GET_AUDITS,
    payload
  });
};

// set currentPage actions
export const SET_AUDITS_CURRENT_PAGE = 'AUDIT.SET_AUDITS_CURRENT_PAGE';
export const getAuditsByPage = (pageNumber: number): Thunk => async (dispatch, getState) => {
  const state = getState();
  const requestedPage = auditPagesSelector(state)[pageNumber];

  if (!requestedPage) {
    const totalRowCount = auditRowCountSelector(state);
    const options = auditLogFilterOptionsSelector(state);
    await dispatch(getAudits({ pageNumber, totalRowCount, options }));
  }

  dispatch({
    type: SET_AUDITS_CURRENT_PAGE,
    payload: { pageNumber }
  });
};

export const RESET = 'AUDIT.RESET';

export const reset = () => ({ type: RESET });

export const SET_FILTER_OPTIONS = 'AUDIT.SET_FILTER_OPTIONS';

export const setFilterOptions = (options: AuditLogOptions) => (dispatch: RootDispatch) => {
  dispatch({ type: SET_FILTER_OPTIONS, payload: options });
};

export const DOWNLOAD_AUDIT_LOG_FILTERS = 'AUDIT.DOWNLOAD_AUDIT_LOG_FILTERS';
export const DOWNLOAD_AUDIT_LOG_FILTERS_SUCCESS = 'AUDIT.DOWNLOAD_AUDIT_LOG_FILTERS_SUCCESS';
export const DOWNLOAD_AUDIT_LOG_FILTERS_FAIL = 'AUDIT.DOWNLOAD_AUDIT_LOG_FILTERS_FAIL';

export const downloadAuditLogFilters = () => async (
  dispatch: Dispatch<any>,
  getState: () => RootState
) => {
  const state = getState();

  // Check for in audit downloads if there are any then return
  if (auditLogFiltersDownloadStatus(state) === 'pending') {
    return;
  }

  dispatch({ type: DOWNLOAD_AUDIT_LOG_FILTERS });
  const newAuditDownload = Config.AUDIT_LOG_EXPANSION_DOWNLOAD;
  const AUDIT_DOWNLOAD_URL = getDispatcherUrl(
    DispatcherCategoryService.UTILITY,
    DispatcherAuthPrefix.AUTHENTICATED,
    DispatcherResource.AUDIT,
    DispatcherEndpoint.DOWNLOAD,
    true
  );
  const patientId = fhirCurrentUserPatientId(state);
  const consumerId = currentAccountConsumerIdSelector(state);
  const accessToken = dfdAccessTokenSelector(state);
  const apikey = apiKeySelector(state);
  const deviceId = DeviceInfoServices.getDeviceId();
  const options = auditLogFilterOptionsSelector(state);
  const filterParams = getFilterParams(options, state);
  const searchParams = getParamsString({ ...filterParams.filterParams });
  const url = newAuditDownload ? AUDIT_DOWNLOAD_URL : `${Config.AUDIT_BASE_URL}/download`;
  const authHeaders = {
    [DFDHeaders.mhpAdapter]: AdapterSource.MHP
  };
  const newHeaders = newAuditDownload ? authHeaders : {};
  const urlParams = newAuditDownload
    ? ''
    : `?${encodeURI(searchParams)}&startsAt=${filterParams.startsAt}&endsAt=${
        filterParams.endsAt
      }&pageNumber=1`;
  const newBody = {
    ...filterParams.filterParams,
    startsAt: filterParams.startsAt,
    endsAt: filterParams.endsAt
  };
  const body = newAuditDownload ? newBody : {};
  const method = newAuditDownload ? 'POST' : 'GET';
  const responseType = newAuditDownload ? 'json' : 'blob';

  try {
    const response = await FileServices.download(
      `audit-log-${consumerId}${Platform.OS === 'android' ? '.txt' : '.csv'}`,
      `${url}${urlParams}`,
      {
        [DFDHeaders.authorization]: `Bearer ${accessToken}`,
        apikey,
        [DFDHeaders.mobileDeviceId]: deviceId,
        [DFDHeaders.consumerId]: consumerId,
        [DFDHeaders.patientId]: patientId,
        ...newHeaders
      },
      undefined,
      method,
      body,
      responseType,
      newAuditDownload
    );

    dispatch({
      type: DOWNLOAD_AUDIT_LOG_FILTERS_SUCCESS,
      payload: { response }
    });
  } catch (err) {
    dispatch({ type: DOWNLOAD_AUDIT_LOG_FILTERS_FAIL, error: err });
  }
};

export const SEND_AUDIT_LOG = 'AUDIT.SEND_AUDIT_LOG';
export const SEND_AUDIT_LOG_SUCCESS = 'AUDIT.SEND_AUDIT_LOG_SUCCESS';
export const SEND_AUDIT_LOG_FAIL = 'AUDIT.SEND_AUDIT_LOG_FAIL';

export function sendAuditLog(data: AuditData): Action {
  const useExpansion = Config.AUDIT_LOG_EXPANSION;

  const EXPANSION_URL = getDispatcherUrl(
    DispatcherCategoryService.UTILITY,
    DispatcherAuthPrefix.AUTHENTICATED,
    DispatcherResource.AUDIT
  );

  const method = useExpansion ? 'post' : 'put';
  const url = useExpansion ? EXPANSION_URL : `${Config.AUDIT_BASE_URL}/audit`;
  const headers = useExpansion
    ? {
        [DFDHeaders.mhpAdapter]: AdapterSource.MHP,
        [DFDHeaders.contentType]: 'application/json'
      }
    : {};

  return {
    type: SEND_AUDIT_LOG,
    payload: {
      client: 'dispatcher',
      request: {
        method,
        url,
        headers,
        data
      }
    }
  };
}
